/**
 * RadioPlayer - Ambient music radio for Airline Ops
 * Uses React Context so audio persists across page navigation
 * Defaults to ON - players can opt-out via pause menu
 */

import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";

// Track list - files should be in /public/music/
// First track is small (1.8MB) for fast startup
const TRACKS = [
  { file: "./music/Velvet Ledger.ogg", name: "Velvet Ledger" },
  { file: "./music/Late Shift Radar - 1.ogg", name: "Late Shift Radar" },
  { file: "./music/Late Shift Radar - 2.ogg", name: "Late Shift Radar II" },
  { file: "./music/Midnight Gate 23 - 1.ogg", name: "Midnight Gate 23" },
  { file: "./music/Midnight Gate 23 - 2.ogg", name: "Midnight Gate 23 II" },
  { file: "./music/Runway Sunbreak - 1.ogg", name: "Runway Sunbreak" },
  { file: "./music/Runway Sunbreak - 2.ogg", name: "Runway Sunbreak II" },
  { file: "./music/Runway Briefing - 1.ogg", name: "Runway Briefing" },
  { file: "./music/Runway Briefing - 2.ogg", name: "Runway Briefing II" },
  { file: "./music/Gate A23 Sunrise - 1.ogg", name: "Gate A23 Sunrise" },
  { file: "./music/Gate A23 Sunrise - 2.ogg", name: "Gate A23 Sunrise II" },
  { file: "./music/Cruising Above The Weather - 1.ogg", name: "Cruising Above The Weather" },
  { file: "./music/Cruising Above The Weather - 2.ogg", name: "Cruising Above The Weather II" },
  { file: "./music/Golden Hour on the Ramp - 1.ogg", name: "Golden Hour on the Ramp" },
  { file: "./music/Golden Hour on the Ramp - 2.ogg", name: "Golden Hour on the Ramp II" },
  { file: "./music/Held at the Edge - 1.ogg", name: "Held at the Edge" },
  { file: "./music/Held at the Edge - 2.ogg", name: "Held at the Edge II" },
  { file: "./music/Hotel Room, 3 A.M. - 1.ogg", name: "Hotel Room, 3 A.M." },
  { file: "./music/Hotel Room, 3 A.M. - 2.ogg", name: "Hotel Room, 3 A.M. II" },
  { file: "./music/Mahogany Metrics - 1.ogg", name: "Mahogany Metrics" },
  { file: "./music/Mahogany Metrics - 2.ogg", name: "Mahogany Metrics II" },
  { file: "./music/Pixelated Supply Chain - 1.ogg", name: "Pixelated Supply Chain" },
  { file: "./music/Pixelated Supply Chain - 2.ogg", name: "Pixelated Supply Chain II" },
  { file: "./music/Spin of the Sky - 1.ogg", name: "Spin of the Sky" },
  { file: "./music/Spin of the Sky - 2.ogg", name: "Spin of the Sky II" },
  // New tracks
  { file: "./music/Sunset on Your Network (1).ogg", name: "Sunset on Your Network" },
  { file: "./music/Sunset on Your Network.ogg", name: "Sunset on Your Network II" },
  { file: "./music/Empire Moves in Silence (1).ogg", name: "Empire Moves in Silence" },
  { file: "./music/Empire Moves in Silence.ogg", name: "Empire Moves in Silence II" },
  { file: "./music/Runway Reverie (1).ogg", name: "Runway Reverie" },
  { file: "./music/Runway Reverie.ogg", name: "Runway Reverie II" },
  { file: "./music/Cash Flow Glow (1).ogg", name: "Cash Flow Glow" },
  { file: "./music/Cash Flow Glow.ogg", name: "Cash Flow Glow II" },
  { file: "./music/Midnight Quiet Win (1).ogg", name: "Midnight Quiet Win" },
  { file: "./music/Midnight Quiet Win.ogg", name: "Midnight Quiet Win II" },
  { file: "./music/Velvet Ledger (1).ogg", name: "Velvet Ledger II" },
  { file: "./music/Earned This View (1).ogg", name: "Earned This View" },
  { file: "./music/Earned This View.ogg", name: "Earned This View II" },
  { file: "./music/One Flight at a Time (1).ogg", name: "One Flight at a Time" },
  { file: "./music/One Flight at a Time.ogg", name: "One Flight at a Time II" },
  { file: "./music/Sunset Operations (1).ogg", name: "Sunset Operations" },
  { file: "./music/Sunset Operations.ogg", name: "Sunset Operations II" },
  { file: "./music/Soft Alarm in Clear Skies (1).ogg", name: "Soft Alarm in Clear Skies" },
  { file: "./music/Soft Alarm in Clear Skies.ogg", name: "Soft Alarm in Clear Skies II" },
  { file: "./music/Midnight Over Water (1).ogg", name: "Midnight Over Water" },
  { file: "./music/Midnight Over Water.ogg", name: "Midnight Over Water II" },
  { file: "./music/Mahogany Tension (1).ogg", name: "Mahogany Tension" },
  { file: "./music/Mahogany Tension.ogg", name: "Mahogany Tension II" },
  { file: "./music/Mahogany Table (1).ogg", name: "Mahogany Table" },
  { file: "./music/Mahogany Table.ogg", name: "Mahogany Table II" },
  { file: "./music/Soft Green Numbers (1).ogg", name: "Soft Green Numbers" },
  { file: "./music/Soft Green Numbers.ogg", name: "Soft Green Numbers II" },
  { file: "./music/First Flight Of The Day (1).ogg", name: "First Flight Of The Day" },
  { file: "./music/First Flight Of The Day.ogg", name: "First Flight Of The Day II" },
  // Trapsoul & New Additions (Jan 2026)
  { file: "./music/Cleared For Takeoff (1).ogg", name: "Cleared For Takeoff" },
  { file: "./music/Cleared For Takeoff.ogg", name: "Cleared For Takeoff II" },
  { file: "./music/Day 14 (EOS) (1).ogg", name: "Day 14 (EOS)" },
  { file: "./music/Day 14 (EOS).ogg", name: "Day 14 (EOS) II" },
  { file: "./music/Empire In My Name.ogg", name: "Empire In My Name" },
  { file: "./music/Final Approach (1).ogg", name: "Final Approach" },
  { file: "./music/Final Approach.ogg", name: "Final Approach II" },
  { file: "./music/Fleet of My Own (1).ogg", name: "Fleet of My Own" },
  { file: "./music/Fleet of My Own.ogg", name: "Fleet of My Own II" },
  { file: "./music/Holding Pattern (1).ogg", name: "Holding Pattern" },
  { file: "./music/Holding Pattern.ogg", name: "Holding Pattern II" },
  { file: "./music/Mythic Trapsoul (1).ogg", name: "Mythic Trapsoul" },
  { file: "./music/Mythic Trapsoul.ogg", name: "Mythic Trapsoul II" },
  { file: "./music/Red Eye Trapsoul (1).ogg", name: "Red Eye Trapsoul" },
  { file: "./music/Red Eye Trapsoul.ogg", name: "Red Eye Trapsoul II" },
  { file: "./music/Running On Fumes (1).ogg", name: "Running On Fumes" },
  { file: "./music/Running On Fumes.ogg", name: "Running On Fumes II" },
  { file: "./music/Turbulence Trapsoul (1).ogg", name: "Turbulence Trapsoul" },
  { file: "./music/Turbulence Trapsoul.ogg", name: "Turbulence Trapsoul II" },
  // Slow Burn & Ambient (Jan 2026)
  { file: "./music/Slow Burn to the Sun.ogg", name: "Slow Burn to the Sun" },
  { file: "./music/Slow Burn to the Sun (1).ogg", name: "Slow Burn to the Sun II" },
  { file: "./music/Barely Breathing in the Blue.ogg", name: "Barely Breathing in the Blue" },
  { file: "./music/Barely Breathing in the Blue (1).ogg", name: "Barely Breathing in the Blue II" },
  { file: "./music/Frozen Between Heartbeats.ogg", name: "Frozen Between Heartbeats" },
  { file: "./music/Frozen Between Heartbeats (1).ogg", name: "Frozen Between Heartbeats II" },
  { file: "./music/Slow Burn Confession.ogg", name: "Slow Burn Confession" },
  { file: "./music/Monumental Slow Burn.ogg", name: "Monumental Slow Burn" },
  { file: "./music/Ghost in My Throat.ogg", name: "Ghost in My Throat" },
  { file: "./music/Ghost in My Throat (1).ogg", name: "Ghost in My Throat II" },
  { file: "./music/Glass Between Us.ogg", name: "Glass Between Us" },
  { file: "./music/Glass Between Us (1).ogg", name: "Glass Between Us II" },
  { file: "./music/Ghost in My Voicemail.ogg", name: "Ghost in My Voicemail" },
  { file: "./music/Gravitate Slow.ogg", name: "Gravitate Slow" },
  { file: "./music/Slow Devastation.ogg", name: "Slow Devastation" },
  { file: "./music/Slow Devastation (1).ogg", name: "Slow Devastation II" },
];

const STORAGE_KEY = "airlineOpsRadioEnabled";
const VOLUME = 0.35;
const FADE_DURATION = 100; // Near-instant fade

// Context for global radio state
const RadioContext = createContext(null);

export function useRadio() {
  const context = useContext(RadioContext);
  if (!context) {
    // Return dummy functions if used outside provider
    return { isPlaying: false, toggle: () => {}, play: () => {}, stop: () => {}, skip: () => {}, currentTrack: null };
  }
  return context;
}

// Provider component - renders no UI, just manages audio
export function RadioProvider({ children }) {
  // Default ON unless user explicitly disabled (localStorage === "false")
  const [isPlaying, setIsPlaying] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== "false";
    } catch {
      return true;
    }
  });
  // Start with track 0 for opening, then random after
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);

  // Setup audio element (once, persists across navigation)
  // IMPORTANT: Initialize immediately for fast startup
  useEffect(() => {
    const audio = new Audio();
    audio.loop = false;
    audio.volume = VOLUME; // Start at target volume, no slow fade
    audio.preload = "auto"; // Preload for faster start
    audioRef.current = audio;

    // Preload the first track so it's ready when user enables radio
    audio.src = TRACKS[0].file;
    audio.load();

    const handleEnded = () => {
      // Pick random track, avoiding current one
      setCurrentTrackIndex(prev => {
        let next;
        do {
          next = Math.floor(Math.random() * TRACKS.length);
        } while (next === prev && TRACKS.length > 1);
        return next;
      });
    };

    const handleError = () => {
      console.warn("[Radio] Track failed, trying random");
      setCurrentTrackIndex(prev => {
        let next;
        do {
          next = Math.floor(Math.random() * TRACKS.length);
        } while (next === prev && TRACKS.length > 1);
        return next;
      });
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.pause();
      audio.src = "";
    };
  }, []);

  // Load track when index changes
  useEffect(() => {
    if (!audioRef.current) return;

    const track = TRACKS[currentTrackIndex];
    if (!track) return;

    audioRef.current.src = track.file;
    audioRef.current.load();

    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    }
  }, [currentTrackIndex]);

  // Handle play/pause - instant start, no fade delay
  useEffect(() => {
    if (!audioRef.current) return;

    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
    }

    if (isPlaying) {
      // Play immediately at target volume
      audioRef.current.volume = VOLUME;
      audioRef.current.play().catch(e => {
        console.warn("[Radio] Play blocked by browser, will retry on first click");
        // Wait for user interaction to start
        const retryPlay = () => {
          if (audioRef.current && isPlaying) {
            audioRef.current.volume = VOLUME;
            audioRef.current.play().catch(() => {});
          }
          document.removeEventListener('click', retryPlay);
          document.removeEventListener('keydown', retryPlay);
        };
        document.addEventListener('click', retryPlay, { once: true });
        document.addEventListener('keydown', retryPlay, { once: true });
      });
    } else {
      // Stop immediately
      audioRef.current.pause();
      audioRef.current.volume = 0;
    }

    localStorage.setItem(STORAGE_KEY, isPlaying ? "true" : "false");
  }, [isPlaying]);

  const toggle = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const play = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const stop = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const skip = useCallback(() => {
    setCurrentTrackIndex(prev => {
      let next;
      do {
        next = Math.floor(Math.random() * TRACKS.length);
      } while (next === prev && TRACKS.length > 1);
      return next;
    });
  }, []);

  const value = {
    isPlaying,
    toggle,
    play,
    stop,
    skip,
    currentTrack: TRACKS[currentTrackIndex],
    trackNumber: currentTrackIndex + 1,
    totalTracks: TRACKS.length,
  };

  return (
    <RadioContext.Provider value={value}>
      {children}
    </RadioContext.Provider>
  );
}

// Toggle button component - can be placed anywhere
export function RadioToggle() {
  const { isPlaying, toggle, skip, currentTrack } = useRadio();

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all"
        style={{
          backgroundColor: isPlaying ? "rgba(96, 255, 208, 0.1)" : "transparent",
          color: isPlaying ? "#60ffd0" : "#525252",
          border: `1px solid ${isPlaying ? "rgba(96, 255, 208, 0.3)" : "#333"}`,
        }}
        title={isPlaying ? `Now playing: ${currentTrack?.name}` : "Radio OFF"}
      >
        <span style={{ fontSize: "10px" }}>
          {isPlaying ? "●" : "○"}
        </span>
        <span>RADIO</span>
        {isPlaying && (
          <span
            className="animate-pulse"
            style={{ fontSize: "8px", marginLeft: "2px" }}
          >
            ♪
          </span>
        )}
      </button>
      {isPlaying && (
        <button
          onClick={skip}
          className="px-1.5 py-1 rounded text-xs transition-all"
          style={{
            backgroundColor: "rgba(96, 255, 208, 0.1)",
            color: "#60ffd0",
            border: "1px solid rgba(96, 255, 208, 0.3)",
          }}
          title="Skip track"
        >
          ⏭
        </button>
      )}
    </div>
  );
}

// Full player with track info (optional, for dedicated UI)
export default function RadioPlayer() {
  const { isPlaying, toggle, skip, currentTrack, trackNumber, totalTracks } = useRadio();

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-lg"
      style={{
        backgroundColor: "#0c0c0c",
        border: "1px solid #1a1a1a",
      }}
    >
      <button
        onClick={toggle}
        className="w-8 h-8 flex items-center justify-center rounded-full transition-all"
        style={{
          backgroundColor: isPlaying ? "#60ffd0" : "#333",
          color: isPlaying ? "#000" : "#737373",
        }}
      >
        {isPlaying ? "❚❚" : "▶"}
      </button>

      <div className="flex-1 min-w-0">
        <div
          className="text-xs font-medium truncate"
          style={{ color: isPlaying ? "#e5e5e5" : "#525252" }}
        >
          {isPlaying ? currentTrack?.name || "Loading..." : "Radio OFF"}
        </div>
        <div className="text-xs" style={{ color: "#525252" }}>
          {isPlaying && `Track ${trackNumber}/${totalTracks}`}
        </div>
      </div>

      {isPlaying && (
        <button
          onClick={skip}
          className="px-2 py-1 text-xs rounded"
          style={{ color: "#737373" }}
        >
          ▶▶
        </button>
      )}

      {isPlaying && (
        <div className="flex items-end gap-0.5 h-4">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="w-1 rounded-full"
              style={{
                backgroundColor: "#60ffd0",
                height: `${40 + Math.random() * 60}%`,
                animation: `radioBar 0.5s ease-in-out infinite alternate`,
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes radioBar {
          0% { height: 20%; }
          100% { height: 100%; }
        }
      `}</style>
    </div>
  );
}
